import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ChevronLeft, X, Lock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

// ─── Step content constants ──────────────────────────────────────────────

interface PurposeOption {
  id: string;
  label: string;
  fenceDefault: number;
}

const PURPOSE_OPTIONS: PurposeOption[] = [
  { id: 'calmer',   label: 'I want a calmer, less overwhelming feed',     fenceDefault: 4 },
  { id: 'informed', label: 'I want to stay informed without the noise',   fenceDefault: 3 },
  { id: 'monitor',  label: 'I want to monitor topics or communities',     fenceDefault: 3 },
  { id: 'family',   label: 'I want a safer feed for my family',           fenceDefault: 5 },
  { id: 'explore',  label: 'Just exploring',                              fenceDefault: 3 },
];

// Free platforms — connectable immediately at no cost
const FREE_PLATFORMS = ['Bluesky', 'YouTube'];
// Paid platforms — available but require a Personal or Family plan
const PAID_PLATFORMS = ['Reddit', 'X (Twitter)'];
// Not yet built
const COMING_SOON_PLATFORMS = [
  'Mastodon', 'Threads', 'Hacker News',
  'Instagram', 'Facebook', 'TikTok', 'LinkedIn',
  'Pinterest', 'Nextdoor',
];

const MORE_TAGS = [
  'Technology', 'Science', 'Politics', 'Parenting', 'Mental Health',
  'Finance', 'Art & Design', 'Sports', 'Local News', 'Business',
  'Education', 'Gaming', 'Food & Cooking', 'Travel', 'Environment',
  'Philosophy', 'Books & Literature',
];

const LESS_TAGS = [
  'Political arguments', 'Outrage & controversy', 'Celebrity gossip',
  'Misinformation', 'Spam', 'Negativity', 'Advertising', 'Clickbait',
  'Sports scores', 'Crypto & NFTs', 'Diet & weight loss content',
];

const TOTAL_STEPS = 6;

// ─── Helpers ─────────────────────────────────────────────────────────────

function TagChips({
  options,
  selected,
  onToggle,
}: {
  options: string[];
  selected: Set<string>;
  onToggle: (tag: string) => void;
}) {
  return (
    <div className="flex flex-wrap gap-2">
      {options.map((opt) => {
        const active = selected.has(opt);
        return (
          <button
            key={opt}
            type="button"
            onClick={() => onToggle(opt)}
            className={`rounded-full border px-3 py-1.5 text-sm transition-colors ${
              active
                ? 'border-foreground bg-foreground text-background'
                : 'border-border bg-background hover:bg-muted/40'
            }`}
          >
            {opt}
          </button>
        );
      })}
    </div>
  );
}

function CustomChipInput({
  values,
  onChange,
  placeholder,
}: {
  values: string[];
  onChange: (next: string[]) => void;
  placeholder: string;
}) {
  const [draft, setDraft] = useState('');
  const add = () => {
    const t = draft.trim();
    if (!t) return;
    if (values.includes(t)) {
      setDraft('');
      return;
    }
    onChange([...values, t]);
    setDraft('');
  };
  return (
    <div>
      <Input
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ',') {
            e.preventDefault();
            add();
          }
        }}
        onBlur={add}
        placeholder={placeholder}
      />
      {values.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-2">
          {values.map((v) => (
            <Badge key={v} variant="outline" className="gap-1 pr-1">
              <span className="text-xs">{v}</span>
              <button
                type="button"
                onClick={() => onChange(values.filter((x) => x !== v))}
                className="rounded-full p-0.5 hover:bg-muted"
                aria-label={`Remove ${v}`}
              >
                <X className="w-3 h-3" />
              </button>
            </Badge>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Wizard ──────────────────────────────────────────────────────────────

export const OnboardingWizard: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [checkedExisting, setCheckedExisting] = useState(false);

  // Step state
  const [purpose, setPurpose] = useState<string>('');
  const [connectedPlatforms, setConnectedPlatforms] = useState<Set<string>>(new Set());
  const [paidPlatformInterest, setPaidPlatformInterest] = useState<Set<string>>(new Set());
  const [interestPlatforms, setInterestPlatforms] = useState<Set<string>>(new Set());
  const [moreTags, setMoreTags] = useState<Set<string>>(new Set());
  const [moreCustom, setMoreCustom] = useState<string[]>([]);
  const [lessTags, setLessTags] = useState<Set<string>>(new Set());
  const [lessCustom, setLessCustom] = useState<string[]>([]);
  const [agreedTos, setAgreedTos] = useState(false);
  const [researchOptIn, setResearchOptIn] = useState(false);
  const [vaultPin, setVaultPin] = useState('');
  const [vaultPinConfirm, setVaultPinConfirm] = useState('');

  // Skip if user already onboarded. Fast path: handleFinish set the
  // sessionStorage flag right before navigating; if we somehow ended up
  // on /onboarding anyway, bounce straight back without a DB read.
  useEffect(() => {
    if (!user?.id) {
      setCheckedExisting(true);
      return;
    }
    try {
      if (sessionStorage.getItem('onboarding-just-completed') === '1') {
        navigate('/dashboard', { replace: true });
        return;
      }
    } catch {}
    supabase
      .from('profiles')
      .select('onboarding_completed')
      .eq('id', user.id)
      .maybeSingle()
      .then(({ data }) => {
        if ((data as any)?.onboarding_completed) {
          navigate('/dashboard', { replace: true });
        } else {
          setCheckedExisting(true);
        }
      });
  }, [user?.id, navigate]);

  const toggle = (set: Set<string>, value: string, setter: (next: Set<string>) => void) => {
    const next = new Set(set);
    if (next.has(value)) next.delete(value);
    else next.add(value);
    setter(next);
  };

  const purposeMeta = useMemo(
    () => PURPOSE_OPTIONS.find((p) => p.id === purpose),
    [purpose],
  );

  const canProceed = (): boolean => {
    if (step === 0) return Boolean(purpose);
    if (step === 1) return connectedPlatforms.size > 0 || paidPlatformInterest.size > 0 || interestPlatforms.size > 0;
    if (step === 2) return true;
    if (step === 3) return true;
    if (step === 4) return true; // vault PIN is optional
    if (step === 5) return agreedTos;
    return false;
  };

  const handleFinish = async () => {
    if (!user?.id) {
      toast.error('Sign in required.');
      navigate('/auth', { replace: true });
      return;
    }
    if (!agreedTos) {
      toast.error('Please accept the Terms of Service to continue.');
      return;
    }
    setSubmitting(true);

    try {
      let region: string | null = null;
      try {
        region = Intl.DateTimeFormat().resolvedOptions().timeZone || null;
      } catch {}

      const profileUpsert: Record<string, any> = {
        id: user.id,
        email: user.email ?? null,
        full_name:
          (user.user_metadata as any)?.full_name ??
          (user.user_metadata as any)?.name ??
          null,
        avatar_url:
          (user.user_metadata as any)?.avatar_url ??
          (user.user_metadata as any)?.picture ??
          null,
        onboarding_completed: true,
        research_opt_in: researchOptIn,
      };
      if (researchOptIn) {
        profileUpsert.research_opt_in_at = new Date().toISOString();
        profileUpsert.research_opt_in_region = region;
      }
      const { error: profileErr } = await supabase
        .from('profiles')
        .upsert(profileUpsert, { onConflict: 'id' });
      if (profileErr) {
        console.warn('[onboarding] profile upsert failed:', profileErr.message);
      }

      const prefsPayload = {
        primary_use_case: purpose,
        platform_priority: Array.from(connectedPlatforms),
        dashboard_layout: 'overview',
        content_filters: [] as string[],
        monitoring_keywords: [] as string[],
        completed_onboarding: true,
        onboarding_purpose: purpose,
        platform_interest: [
          ...Array.from(interestPlatforms),
          ...Array.from(paidPlatformInterest),
        ],
      };

      const { data: existing } = await supabase
        .from('user_preferences')
        .select('id')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (existing?.id) {
        const { error: updateErr } = await supabase
          .from('user_preferences')
          .update(prefsPayload)
          .eq('id', existing.id);
        if (updateErr) throw updateErr;
      } else {
        const { error: insertErr } = await supabase
          .from('user_preferences')
          .insert({ user_id: user.id, ...prefsPayload });
        if (insertErr) throw insertErr;
      }

      const listeningSources = [...Array.from(moreTags), ...moreCustom];
      const fenceSources = [...Array.from(lessTags), ...lessCustom];
      const fenceGauge = purposeMeta?.fenceDefault ?? 3;

      const rulesRows: any[] = [];
      for (const tag of listeningSources) {
        rulesRows.push({
          user_id: user.id,
          name: tag,
          rule_type: 'listening',
          keywords: [tag],
          intensity_gauge: 3,
          is_active: true,
          highlight_style: { badge: true },
        });
      }
      for (const tag of fenceSources) {
        rulesRows.push({
          user_id: user.id,
          name: tag,
          rule_type: 'filter',
          keywords: [tag],
          intensity_gauge: fenceGauge,
          intensity: fenceGauge >= 3 ? 'hide' : 'deemphasize',
          is_active: true,
        });
      }

      if (rulesRows.length) {
        const { error: rulesErr } = await supabase.from('filtering_rules').insert(rulesRows);
        if (rulesErr) {
          console.warn('[onboarding] seed rules failed:', rulesErr.message);
        }
      }

      // Save vault PIN if the user chose to set one during onboarding
      if (vaultPin.length === 4 && vaultPin === vaultPinConfirm) {
        try {
          await supabase.rpc('set_parent_lock_pin', { p_pin: vaultPin });
        } catch (pinErr) {
          console.warn('[onboarding] vault PIN save failed:', pinErr);
          // Non-fatal — user can set PIN later in Settings → Family
        }
      }

      // Drop a sessionStorage breadcrumb so OnboardingCheck on /dashboard
      // doesn't have to re-read the DB to know we just finished. The flag is
      // consumed on first read, so it can't persist into a future session
      // and incorrectly skip the check.
      try {
        sessionStorage.setItem('onboarding-just-completed', '1');
      } catch {}

      toast.success('Welcome to SocialScoop.');
      navigate('/dashboard?welcome=1', { replace: true });
    } catch (err) {
      console.error('[onboarding] finish failed:', err);
      toast.error(err instanceof Error ? err.message : 'Setup failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!checkedExisting) {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{ backgroundColor: '#FAFAF8' }}
      >
        <div className="text-sm text-muted-foreground">Loading…</div>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen flex items-start sm:items-center justify-center px-4 py-12 font-sans"
      style={{ backgroundColor: '#FAFAF8' }}
    >
      <div className="w-full max-w-[600px]">
        <div className="mb-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs uppercase tracking-wider text-muted-foreground">
              Step {step + 1} of {TOTAL_STEPS}
            </span>
            {step > 0 && (
              <button
                type="button"
                onClick={() => setStep((s) => Math.max(0, s - 1))}
                className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
              >
                <ChevronLeft className="w-3 h-3" />
                Back
              </button>
            )}
          </div>
          <div className="h-1 bg-border/60 rounded-full overflow-hidden">
            <div
              className="h-full bg-foreground transition-all duration-300"
              style={{ width: `${((step + 1) / TOTAL_STEPS) * 100}%` }}
            />
          </div>
        </div>

        <Card className="border border-border shadow-none">
          <CardContent className="p-8 sm:p-10">
            {step === 0 && <StepPurpose value={purpose} onChange={setPurpose} />}
            {step === 1 && (
              <StepPlatforms
                connected={connectedPlatforms}
                paidInterest={paidPlatformInterest}
                interest={interestPlatforms}
                onToggleConnected={(v) => toggle(connectedPlatforms, v, setConnectedPlatforms)}
                onTogglePaidInterest={(v) => toggle(paidPlatformInterest, v, setPaidPlatformInterest)}
                onToggleInterest={(v) => toggle(interestPlatforms, v, setInterestPlatforms)}
              />
            )}
            {step === 2 && (
              <StepMore
                tags={moreTags}
                custom={moreCustom}
                onToggleTag={(v) => toggle(moreTags, v, setMoreTags)}
                onChangeCustom={setMoreCustom}
              />
            )}
            {step === 3 && (
              <StepLess
                tags={lessTags}
                custom={lessCustom}
                onToggleTag={(v) => toggle(lessTags, v, setLessTags)}
                onChangeCustom={setLessCustom}
              />
            )}
            {step === 4 && (
              <StepVaultPin
                pin={vaultPin}
                confirmPin={vaultPinConfirm}
                onPin={setVaultPin}
                onConfirmPin={setVaultPinConfirm}
              />
            )}
            {step === 5 && (
              <StepFinish
                agreedTos={agreedTos}
                onAgreedTos={setAgreedTos}
                researchOptIn={researchOptIn}
                onResearchOptIn={setResearchOptIn}
              />
            )}

            <div className="mt-10 flex justify-end">
              {step < TOTAL_STEPS - 1 ? (
                <Button
                  onClick={() => setStep((s) => Math.min(TOTAL_STEPS - 1, s + 1))}
                  disabled={!canProceed()}
                >
                  Continue
                </Button>
              ) : (
                <Button onClick={handleFinish} disabled={!canProceed() || submitting}>
                  {submitting ? 'Setting up…' : 'Take me to my feed →'}
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// ─── Steps ───────────────────────────────────────────────────────────────

const StepPurpose: React.FC<{ value: string; onChange: (v: string) => void }> = ({
  value,
  onChange,
}) => (
  <div className="space-y-6">
    <div>
      <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight">What brings you here?</h1>
      <p className="mt-2 text-sm text-muted-foreground">
        Pick the one that fits best. We'll tune your defaults around it.
      </p>
    </div>
    <div className="space-y-2">
      {PURPOSE_OPTIONS.map((opt) => {
        const active = value === opt.id;
        return (
          <button
            key={opt.id}
            type="button"
            onClick={() => onChange(opt.id)}
            className={`w-full text-left rounded-lg border p-4 transition-colors ${
              active ? 'border-foreground bg-muted/40' : 'border-border hover:bg-muted/20'
            }`}
          >
            <span className="text-base">{opt.label}</span>
          </button>
        );
      })}
    </div>
  </div>
);

const StepPlatforms: React.FC<{
  connected: Set<string>;
  paidInterest: Set<string>;
  interest: Set<string>;
  onToggleConnected: (v: string) => void;
  onTogglePaidInterest: (v: string) => void;
  onToggleInterest: (v: string) => void;
}> = ({ connected, paidInterest, interest, onToggleConnected, onTogglePaidInterest, onToggleInterest }) => (
  <div className="space-y-6">
    <div>
      <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight">
        Which platforms do you use?
      </h1>
      <p className="mt-2 text-sm text-muted-foreground">
        Select everything relevant. We'll tailor your feed around it.
      </p>
    </div>

    {/* Free platforms */}
    <div>
      <div className="flex items-center gap-2 mb-3">
        <p className="text-xs uppercase tracking-wider text-muted-foreground">Free — connect now</p>
        <span className="text-[10px] bg-green-100 text-green-800 rounded-full px-2 py-0.5 font-medium">Included</span>
      </div>
      <TagChips options={FREE_PLATFORMS} selected={connected} onToggle={onToggleConnected} />
      <p className="mt-2 text-xs text-muted-foreground">Also: RSS &amp; newsletter feeds are free on all plans.</p>
    </div>

    {/* Paid platforms */}
    <div>
      <div className="flex items-center gap-2 mb-3">
        <p className="text-xs uppercase tracking-wider text-muted-foreground">Paid plan — Reddit &amp; X</p>
        <span className="text-[10px] bg-[#1A2E4A]/10 text-[#1A2E4A] rounded-full px-2 py-0.5 font-medium">Personal $9/mo</span>
      </div>
      <TagChips options={PAID_PLATFORMS} selected={paidInterest} onToggle={onTogglePaidInterest} />
      {paidInterest.size > 0 && (
        <p className="mt-2 text-xs text-muted-foreground rounded-lg bg-[#1A2E4A]/[0.04] border border-[#1A2E4A]/10 px-3 py-2">
          Great — we'll remind you to connect {Array.from(paidInterest).join(' and ')} once you're in. You can upgrade any time from the Platforms page.
        </p>
      )}
    </div>

    {/* Coming soon */}
    <div>
      <p className="text-xs uppercase tracking-wider text-muted-foreground mb-3">Coming soon — notify me</p>
      <TagChips options={COMING_SOON_PLATFORMS} selected={interest} onToggle={onToggleInterest} />
      <p className="mt-2 text-xs text-muted-foreground">We'll email you when these go live.</p>
    </div>
  </div>
);

const StepMore: React.FC<{
  tags: Set<string>;
  custom: string[];
  onToggleTag: (v: string) => void;
  onChangeCustom: (next: string[]) => void;
}> = ({ tags, custom, onToggleTag, onChangeCustom }) => (
  <div className="space-y-6">
    <div>
      <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight">
        What do you want more of?
      </h1>
      <p className="mt-2 text-sm text-muted-foreground">
        We'll surface posts that match. Each pick becomes a Listening Rule you can edit later.
      </p>
    </div>

    <TagChips options={MORE_TAGS} selected={tags} onToggle={onToggleTag} />

    <div>
      <Label className="text-sm">Add your own</Label>
      <p className="text-xs text-muted-foreground mb-2">Press Enter or comma to add.</p>
      <CustomChipInput
        values={custom}
        onChange={onChangeCustom}
        placeholder="e.g., woodworking, MTB trails"
      />
    </div>
  </div>
);

const StepLess: React.FC<{
  tags: Set<string>;
  custom: string[];
  onToggleTag: (v: string) => void;
  onChangeCustom: (next: string[]) => void;
}> = ({ tags, custom, onToggleTag, onChangeCustom }) => (
  <div className="space-y-6">
    <div>
      <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight">
        What do you want less of?
      </h1>
      <p className="mt-2 text-sm text-muted-foreground">
        We'll quietly fence these out. Nothing is deleted — items land in your Reviewed Queue so
        you can always look.
      </p>
    </div>

    <TagChips options={LESS_TAGS} selected={tags} onToggle={onToggleTag} />

    <div>
      <Label className="text-sm">Add your own</Label>
      <p className="text-xs text-muted-foreground mb-2">Press Enter or comma to add.</p>
      <CustomChipInput
        values={custom}
        onChange={onChangeCustom}
        placeholder="e.g., specific topics, accounts"
      />
    </div>
  </div>
);

// ─── Step 4: Vault PIN ───────────────────────────────────────────────────────

const StepVaultPin: React.FC<{
  pin: string;
  confirmPin: string;
  onPin: (v: string) => void;
  onConfirmPin: (v: string) => void;
}> = ({ pin, confirmPin, onPin, onConfirmPin }) => {
  const mismatch = confirmPin.length === 4 && pin !== confirmPin;
  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight">
          Protect your Content Vault.
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          SocialScoop never deletes content — everything your rules filter out is saved to a private
          Content Vault. Set a 4-digit PIN now so only you can open it.
        </p>
      </div>

      <div className="rounded-lg border border-[#1A2E4A]/15 bg-[#1A2E4A]/[0.03] p-4 text-sm space-y-1">
        <p className="font-medium text-[#1A2E4A] flex items-center gap-2">
          <Lock className="h-4 w-4" />
          What the PIN protects
        </p>
        <ul className="text-xs text-muted-foreground space-y-0.5 pl-6 list-disc">
          <li>Viewing the Content Vault (filtered posts)</li>
          <li>Optionally: adding or changing filtering rules (turn on in Settings → Family)</li>
        </ul>
      </div>

      <div className="space-y-3 max-w-xs">
        <div>
          <Label htmlFor="ob-pin">Choose a PIN</Label>
          <Input
            id="ob-pin"
            type="password"
            inputMode="numeric"
            pattern="\d{4}"
            maxLength={4}
            placeholder="4 digits"
            value={pin}
            onChange={(e) => onPin(e.target.value.replace(/\D/g, ''))}
            autoComplete="new-password"
          />
        </div>
        <div>
          <Label htmlFor="ob-pin-confirm">Confirm PIN</Label>
          <Input
            id="ob-pin-confirm"
            type="password"
            inputMode="numeric"
            pattern="\d{4}"
            maxLength={4}
            placeholder="4 digits"
            value={confirmPin}
            onChange={(e) => onConfirmPin(e.target.value.replace(/\D/g, ''))}
            autoComplete="new-password"
            className={mismatch ? 'border-destructive' : ''}
          />
          {mismatch && <p className="text-xs text-destructive mt-1">PINs don't match.</p>}
        </div>
      </div>

      <p className="text-xs text-muted-foreground">
        Optional — you can skip this and set a PIN later in{' '}
        <span className="font-medium">Settings → Family</span>.
      </p>
    </div>
  );
};

// ─── Step 5: Finish / ToS ────────────────────────────────────────────────────

const StepFinish: React.FC<{
  agreedTos: boolean;
  onAgreedTos: (v: boolean) => void;
  researchOptIn: boolean;
  onResearchOptIn: (v: boolean) => void;
}> = ({ agreedTos, onAgreedTos, researchOptIn, onResearchOptIn }) => (
  <div className="space-y-6">
    <div>
      <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight">Almost there.</h1>
      <p className="mt-2 text-sm text-muted-foreground">A couple of things before you go in.</p>
    </div>

    <label className="flex items-start gap-3 cursor-pointer rounded-lg border border-border p-4">
      <Checkbox
        checked={agreedTos}
        onCheckedChange={(c) => onAgreedTos(c === true)}
        className="mt-0.5"
      />
      <span className="text-sm leading-relaxed">
        I agree to the{' '}
        <Link
          to="/terms"
          className="underline underline-offset-4 hover:text-foreground"
          target="_blank"
        >
          Terms of Service
        </Link>{' '}
        and{' '}
        <Link
          to="/privacy"
          className="underline underline-offset-4 hover:text-foreground"
          target="_blank"
        >
          Privacy Policy
        </Link>
        . <span className="text-muted-foreground">(Required to continue.)</span>
      </span>
    </label>

    <label className="flex items-start gap-3 cursor-pointer rounded-lg border border-border p-4">
      <Checkbox
        checked={researchOptIn}
        onCheckedChange={(c) => onResearchOptIn(c === true)}
        className="mt-0.5"
      />
      <span className="text-sm leading-relaxed">
        I'd like to contribute anonymized usage data to social media wellbeing research.{' '}
        <span className="text-muted-foreground">
          Optional. Aggregated only — your identity is never shared. You can change this any time
          in Settings → Privacy.
        </span>
      </span>
    </label>
  </div>
);

export default OnboardingWizard;
